import React from 'react'
import styled from '@emotion/styled'
import {
    PRIMARY_DISCOUNT_COLOR_VAR,
    SECONDARY_DISCOUNT_COLOR_VAR,
    SUBTLE_PRIMARY_COLOR_VAR,
} from '../../../context/CustomizationContextProvider'
import { PriceTagIcon, CheckmarkIcon } from '../../icons/Icons'
import { getTypographyRoleStyle } from '../../../style/typography'
import { calculateDiscountPercentage } from '../../../utils/discounts'
import { scaledFontSize } from '../../../style/scrollingLayout'

const DiscountPercentageBadge = styled.span({
    backgroundColor: `var(${PRIMARY_DISCOUNT_COLOR_VAR})`,
    color: `var(${SECONDARY_DISCOUNT_COLOR_VAR})`,
    ...getTypographyRoleStyle('badgeText', {
        fontWeight: 400,
    }),
    fontSize: scaledFontSize(14),
    padding: '2px 6px',
    borderRadius: '4px',
    whiteSpace: 'nowrap',
})

const DiscountAppliedBadge = styled.div({
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    ...getTypographyRoleStyle('statusText'),
    fontSize: scaledFontSize(13),
    color: `var(${SUBTLE_PRIMARY_COLOR_VAR})`,
    marginTop: '2px',
})

// Alias of DiscountAppliedBadge - identical styling
const SaleSavingsText = DiscountAppliedBadge

interface DiscountPercentageBadgeProps {
    comparePrice: number
    finalPrice: number
}

export const DiscountPercentageBadgeComponent: React.FC<
    DiscountPercentageBadgeProps
> = ({ comparePrice, finalPrice }) => {
    const discountPercentage = calculateDiscountPercentage(
        comparePrice,
        finalPrice,
    )

    if (discountPercentage <= 0) {
        return null
    }

    return (
        <DiscountPercentageBadge>
            -{discountPercentage}%
        </DiscountPercentageBadge>
    )
}

interface DiscountAppliedBadgeProps {
    discountCodes: string[]
}

export const DiscountAppliedBadgeComponent: React.FC<
    DiscountAppliedBadgeProps
> = ({ discountCodes }) => {
    if (discountCodes.length === 0) {
        return null
    }

    return (
        <DiscountAppliedBadge>
            <PriceTagIcon
                width={14}
                height={14}
                color={`var(${SUBTLE_PRIMARY_COLOR_VAR})`}
            />
            <span>{discountCodes.join(', ')} applied</span>
        </DiscountAppliedBadge>
    )
}

export const SaleSavingsBadge: React.FC<{ label?: string }> = ({
    label = 'Sale savings',
}) => {
    return (
        <SaleSavingsText>
            <CheckmarkIcon
                width={14}
                height={14}
                color={`var(${SUBTLE_PRIMARY_COLOR_VAR})`}
            />
            <span>{label}</span>
        </SaleSavingsText>
    )
}
